import { FC } from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { ClipboardTextIcon } from '@phosphor-icons/react';
import { classifyRI, RiStatus } from '@leopart/calc';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import { ITrainingBlock } from '../../../models/allenamenti';
import { getRpeBin } from '../../../leopart-update/components/effort/useTeamEffort';
import { ITeamDailyLoadRecovery } from './useTeamDailyLoadRecovery';
import BlockCard from './BlockCard';
import { useBlockClipboard } from './useBlockClipboard';
import styles from './DayColumn.module.scss';

/** Classe del pallino colorato per fascia RI — stessa semantica di RI_VAR in EffortRecoveryRow.tsx. */
const RI_DOT_CLASS: Record<RiStatus, string> = {
  good: styles.riGood,
  normal: styles.riNormal,
  warning: styles.riWarning,
  critical: styles.riCritical,
};

interface IDayColumnProps {
  short: string;
  dayNumber: number;
  iso: string;
  blocks: ITrainingBlock[];
  /** RPE medio squadra + recupero medio squadra del giorno — `undefined` mentre carica. */
  dailyLoad: ITeamDailyLoadRecovery | undefined;
  /** `true` solo se la settimana mostrata contiene oggi ed è questo il giorno. */
  isToday: boolean;
  resolve: (slug: string) => { labelIt: string; color: string } | undefined;
  onAdd: (iso: string) => void;
  onEdit: (block: ITrainingBlock) => void;
  /**
   * Incolla il contenuto degli appunti in fondo a questo giorno (F1). Il
   * bottone che la richiama compare solo a clipboard piena — vedi
   * `useBlockClipboard` qui sotto.
   */
  onPaste: (iso: string) => void;
  /**
   * `true` mentre la creazione da "Incolla" è in volo (hardening
   * double-submit): disabilita il bottone, altrimenti due click ravvicinati
   * creano due blocchi con lo stesso `order`.
   */
  isPasting: boolean;
  /** Passato a ogni BlockCard: vedi useWeekBoardDnd.registerCardNode. */
  registerCardNode: (id: string, node: HTMLDivElement | null) => void;
}

/**
 * Una colonna della settimana: intestazione, barra di composizione, blocchi.
 *
 * È anche zona di drop: `iso` è l'id droppable, così `onDragEnd` in
 * useWeekBoardDnd sa su quale giorno è stato rilasciato il blocco senza
 * risalire dal DOM. Serve per il rilascio sullo spazio VUOTO della colonna —
 * quando invece si rilascia sopra una card, l'`over` è quella card e la
 * posizione è la sua.
 *
 * Il `SortableContext` per giorno è ciò che rende il riordino verticale
 * possibile: dice a dnd-kit quali card competono fra loro dentro questa
 * colonna, e in che ordine.
 */
const DayColumn: FC<IDayColumnProps> = ({
  short,
  dayNumber,
  iso,
  blocks,
  dailyLoad,
  isToday,
  resolve,
  onAdd,
  onEdit,
  onPaste,
  isPasting,
  registerCardNode,
}) => {
  const totalMinutes = blocks.reduce((sum, b) => sum + b.durationMinutes, 0);
  const { setNodeRef, isOver } = useDroppable({ id: iso });
  // Letto direttamente dal contesto (F1): evita di far attraversare
  // `WeekBoard` a un valore che serve solo qui, per decidere se mostrare
  // "Incolla" — la card sorgente lo SCRIVE allo stesso modo, via
  // BlockCardMenu, senza passare da nessun prop drilling.
  const { clipboard } = useBlockClipboard();

  const avgRpe = dailyLoad?.avgRpe ?? null;
  const avgRi = dailyLoad?.avgRi ?? null;
  const rpeDotClass = avgRpe != null ? styles[`rpeBin${getRpeBin(avgRpe)}`] : styles.dotNeutral;
  const riDotClass = avgRi != null ? RI_DOT_CLASS[classifyRI(avgRi)] : styles.dotNeutral;

  const columnClass = [
    styles.column,
    isToday && styles.today,
    isOver && styles.dropTarget,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={setNodeRef} className={columnClass}>
      <div className={styles.head}>
        <div className={styles.headTop}>
          <span className={styles.name}>{short}</span>
          <span className={isToday ? `${styles.date} ${styles.dateToday}` : styles.date}>
            {dayNumber}
          </span>
        </div>

        <div className={styles.bar}>
          {totalMinutes > 0 &&
            blocks.map((b) => (
              <span
                key={b._id}
                className={styles.barSegment}
                style={{
                  flexBasis: `${(b.durationMinutes / totalMinutes) * 100}%`,
                  /*
                    Il colore della PRIMA categoria: la barra rappresenta il
                    TEMPO del giorno, e il tempo non si duplica. Un segmento
                    per ogni categoria del blocco allungherebbe la barra oltre
                    i minuti realmente allenati.
                  */
                  background:
                    resolve(b.categoryIds?.[0] ?? '')?.color ?? 'transparent',
                }}
              />
            ))}
        </div>

        <span className={styles.total}>
          {totalMinutes > 0 ? `${totalMinutes}'` : '—'}
        </span>

        {/* Dato di giorno+squadra (non del singolo blocco): mostrato una
            volta per colonna, non ripetuto sotto ogni allenamento. */}
        <div className={styles.dailyLoad}>
          <span className={styles.dailyLoadItem}>
            {/* Pallino decorativo: la fascia è già nel valore accanto (colore
                non unico veicolo dell'informazione). */}
            <span className={`${styles.dot} ${rpeDotClass}`} aria-hidden="true" />
            RPE medio {avgRpe != null ? avgRpe.toFixed(1) : '—'}
          </span>
          <span className={styles.dailyLoadItem}>
            <span className={`${styles.dot} ${riDotClass}`} aria-hidden="true" />
            Recupero medio {avgRi != null ? avgRi : '—'}
          </span>
        </div>
      </div>

      <div className={styles.blocks}>
        <SortableContext
          items={blocks.map((b) => b._id)}
          strategy={verticalListSortingStrategy}
        >
        {/* Un giorno senza blocchi lo dice già il trattino del totale sopra:
            ripeterlo qui sarebbe la stessa informazione due volte, per sei
            colonne su sette. Lo spazio resta libero per il bottone. */}
        {blocks.length > 0 &&
          blocks.map((block, index) => (
            <BlockCard
              key={block._id}
              block={block}
              index={index}
              categories={(block.categoryIds ?? []).map((id) => ({
                label: resolve(id)?.labelIt ?? id,
                color: resolve(id)?.color,
              }))}
              subcategoryLabels={(block.subcategoryIds ?? []).map(
                (id) => resolve(id)?.labelIt ?? id,
              )}
              onClick={onEdit}
              registerCardNode={registerCardNode}
            />
          ))}
        </SortableContext>
      </div>

      {/* `text` + `xs`: dentro una colonna stretta un bottone pieno pesa
          quanto una card, e questa è un'azione di servizio, non il contenuto.
          "Incolla" (F1) compare accanto solo a clipboard piena, e sparisce
          da solo se `flex-wrap` non trova posto sulla stessa riga. */}
      <div className={styles.footer}>
        <CustomButton
          size="xs"
          color="text"
          icon="plus"
          iconPosition="left"
          fullWidth
          onClick={() => onAdd(iso)}
          className={blocks.length === 0 ? `${styles.add} ${styles.addEmpty}` : styles.add}
        >
          Aggiungi blocco
        </CustomButton>
        {clipboard && (
          <CustomButton
            size="xs"
            color="text"
            icon={<ClipboardTextIcon size={14} />}
            iconPosition="left"
            fullWidth
            disabled={isPasting}
            onClick={() => onPaste(iso)}
            className={styles.add}
            data-testid="day-paste-button"
          >
            Incolla
          </CustomButton>
        )}
      </div>
    </div>
  );
};

export default DayColumn;
